import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { post, ApiError, type LoginOutcome } from '../../lib/api.ts';
import { useRefreshMe } from '../../lib/auth.tsx';
import { isTenantExplicit, isTenantHostLocked, tenantSlug } from '../../lib/tenant.ts';
import { accountMe, enterWorkspace, workspaces } from '../../lib/account.ts';
import { AuthShell, AuthButton, Field, inputCls, ErrorText, PasswordInput, DONE_MS, wait } from '../../components/AuthShell.tsx';
import { LoginNotices } from './LoginNotices.tsx';

type OrgStart = { email: string; mode: 'password' | 'sso'; ssoRequired: boolean; ssoAvailable: boolean; sso?: { available: boolean; required: boolean; displayName: string } };
// CR-065: 콜백이 /login?sso_error= 로 돌려보낼 때의 문구
const SSO_ERRORS: Record<string, string> = { idp_error: 'IdP 가 로그인을 거부했습니다.', state_invalid: '로그인 요청이 만료되었거나 유효하지 않습니다. 다시 시도하세요.', token_invalid: 'IdP 응답을 검증하지 못했습니다. 관리자에게 문의하세요.', email_missing: 'IdP 가 이메일을 주지 않았습니다. 관리자에게 문의하세요.', user_not_registered: '이 조직에 등록되지 않은 계정입니다. 관리자에게 초대를 요청하세요.', user_disabled: '비활성화된 계정입니다.', tenant_suspended: '서비스가 정지되었습니다.', sso_inactive: 'SSO 가 비활성 상태입니다. 비밀번호로 로그인하세요.', account_mismatch: 'IdP 계정의 이메일이 로그인한 계정과 다릅니다.' };

/**
 * 로그인 (CR-067).
 *  - 앱 기본 주소: 이메일 → 본인 확인(비밀번호 | 메일 링크) → 조직 선택. 조직 ID 는 묻지 않는다.
 *  - 조직 주소(커스텀 도메인 · ?tenant=): 그 조직의 로그인 수단(SSO / 이메일)을 고른다. 조직이 이메일·비밀번호 로그인을 끄면 SSO 와 비상 로그인만 남는다.
 */
export function LoginPage() {
  return isTenantExplicit() ? <OrgLogin /> : <AccountLogin />;
}

function SsoErrorBox() {
  const [sp, setSp] = useSearchParams(); const code = sp.get('sso_error');
  if (!code) return null;
  return <div className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-800">{SSO_ERRORS[code] ?? 'SSO 로그인에 실패했습니다.'} <button type="button" className="underline ml-1" onClick={() => { sp.delete('sso_error'); setSp(sp, { replace: true }); }}>닫기</button></div>;
}
function EmailRow({ email, onChange }: { email: string; onChange: () => void }) {
  return (
    <div className="mb-4 flex items-center justify-between border border-slate-200 bg-slate-50 h-9 px-3 text-[13px]">
      <span className="text-slate-700 truncate">{email}</span>
      <button type="button" className="ml-2 flex-none text-[12px] text-accent-strong hover:underline" onClick={onChange}>변경</button>
    </div>
  );
}
const Divider = ({ children }: { children: string }) => <div className="my-3 flex items-center gap-2 text-[11px] text-slate-400"><span className="flex-1 border-t border-slate-200" />{children}<span className="flex-1 border-t border-slate-200" /></div>;

/** 메일로 보낸 링크를 기다리는 화면. 같은 메일의 6자리 코드를 여기서 입력해도 된다 */
function MagicLinkWait({ email, minutes, onVerified, onBack }: { email: string; minutes: number; onVerified: () => Promise<void>; onBack: () => void }) {
  const [code, setCode] = useState(''); const [error, setError] = useState<unknown>(null); const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try { await post('/account/login/magic-link/verify', { email, code }); await onVerified(); }
    catch (err) { setError(err); setBusy(false); }
  }
  return (
    <>
      <div className="border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12.5px] text-sky-900 leading-relaxed">
        <b>{email}</b> 로 로그인 링크를 보냈습니다. 등록된 이메일이면 곧 도착합니다. 이 브라우저에서 링크를 열거나, 메일에 적힌 6자리 코드를 아래에 입력하세요. ({minutes}분 동안 유효)
      </div>
      <form onSubmit={submit} className="mt-4">
        <Field label="메일의 6자리 코드"><input className={inputCls + ' font-mono tracking-widest'} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus /></Field>
        <AuthButton busy={busy} busyText="확인 중…" disabled={code.length !== 6}>코드로 로그인</AuthButton>
        <ErrorText error={error} />
      </form>
      <button type="button" className="mt-3 text-[12px] text-accent-strong hover:underline" onClick={onBack}>다른 방법으로 로그인</button>
    </>
  );
}

/** 앱 기본 주소: 이메일 우선 로그인 */
function AccountLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'verify' | 'link'>('email');
  const [minutes, setMinutes] = useState(10);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<'' | 'pw' | 'link' | 'start'>(''); const [done, setDone] = useState(false);
  // 계정 세션이 살아 있으면 바로 조직 선택으로
  useEffect(() => { accountMe().then(() => nav('/workspaces', { replace: true })).catch(() => undefined); }, [nav]);

  async function onStart(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy('start');
    try { const r = await post<{ email: string }>('/account/login/start', { email }); setEmail(r.email); setStep('verify'); }
    catch (err) { setError(err); } finally { setBusy(''); }
  }
  async function onPassword(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy('pw');
    try { await post('/account/login/password', { email, password }); setBusy(''); setDone(true); await wait(DONE_MS); nav('/workspaces', { replace: true }); }
    catch (err) { setError(err); setBusy(''); }
  }
  async function onLink() {
    setError(null); setBusy('link');
    try { const r = await post<{ minutes: number }>('/account/login/magic-link', { email }); setMinutes(r.minutes); setStep('link'); }
    catch (err) { setError(err); } finally { setBusy(''); }
  }
  const notices = <LoginNotices />;
  const back = () => { setStep('email'); setPassword(''); setError(null); };

  if (step === 'email') {
    return (
      <AuthShell title="로그인" subtitle="업무용 이메일로 계속합니다." noTenant steps={{ total: 3, current: 1 }} below={notices}>
        <form onSubmit={onStart}>
          <Field label="이메일"><input className={inputCls} type="email" autoComplete="username" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></Field>
          <AuthButton busy={busy === 'start'} busyText="확인 중…">다음</AuthButton>
          <ErrorText error={error} />
          <SsoErrorBox />
        </form>
      </AuthShell>
    );
  }
  if (step === 'link') {
    return (
      <AuthShell title="메일을 확인하세요" noTenant steps={{ total: 3, current: 2 }} below={notices}>
        <MagicLinkWait email={email} minutes={minutes} onVerified={async () => nav('/workspaces', { replace: true })} onBack={() => setStep('verify')} />
      </AuthShell>
    );
  }
  return (
    <AuthShell title="본인 확인" subtitle="비밀번호를 입력하거나 메일로 로그인 링크를 받으세요." noTenant steps={{ total: 3, current: 2 }} below={notices}>
      <EmailRow email={email} onChange={back} />
      <form onSubmit={onPassword}>
        <Field label="비밀번호"><PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus /></Field>
        <AuthButton busy={busy === 'pw'} done={done} busyText="로그인 중…" doneText="확인됨">로그인</AuthButton>
      </form>
      <Divider>또는</Divider>
      <AuthButton type="button" className="bg-white! text-slate-700! border border-slate-300 hover:bg-slate-50!" busy={busy === 'link'} busyText="보내는 중…" onClick={onLink}>메일로 로그인 링크 받기</AuthButton>
      <p className="mt-2 text-[11.5px] text-slate-500 leading-relaxed">비밀번호가 없거나 잊었다면 메일 링크로 로그인한 뒤 내 계정에서 새로 정할 수 있습니다.</p>
      <ErrorText error={error} />
    </AuthShell>
  );
}

/** 조직 주소: 그 조직의 로그인 수단을 고른다 */
function OrgLogin() {
  const nav = useNavigate(); const refresh = useRefreshMe(); const qc = useQueryClient();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [start, setStart] = useState<OrgStart | null>(null);
  const [link, setLink] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<'' | 'start' | 'pw' | 'sso' | 'link'>(''); const [done, setDone] = useState(false);
  const [showBreakGlass, setShowBreakGlass] = useState(false);
  const slug = tenantSlug();

  async function onStart(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy('start');
    try { setStart(await post<OrgStart>('/auth/login/start', { email })); }
    catch (err) { setError(describeTenantError(err)); } finally { setBusy(''); }
  }
  async function onPassword(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy('pw');
    try {
      const r = await post<LoginOutcome>('/auth/login/password', { email, password });
      setBusy(''); setDone(true);
      await Promise.all([refresh(), wait(DONE_MS)]);
      nav(r.status === 'ok' ? '/' : r.status === 'mfa_required' ? '/mfa' : '/mfa/enroll', { replace: true });
    } catch (err) { setError(err); setBusy(''); }
  }
  async function onSso() {
    setError(null); setBusy('sso');
    try { const r = await post<{ url: string }>('/auth/sso/oidc/start', { email, returnTo: '/' }); window.location.assign(r.url); }
    catch (err) { setError(err); setBusy(''); }
  }
  async function onLink() {
    setError(null); setBusy('link');
    try { const r = await post<{ minutes: number }>('/account/login/magic-link', { email, tenant: slug }); setLink(r.minutes); }
    catch (err) { setError(err); } finally { setBusy(''); }
  }
  /** 메일 코드로 본인 확인이 끝나면 이 조직으로 바로 들어간다 */
  async function afterLink() {
    const w = (await workspaces()).find((x) => x.slug === slug);
    if (!w || w.status !== 'active') { nav('/workspaces', { replace: true }); return; }
    const to = await enterWorkspace(w.tenantId);
    if (to === null) return;
    qc.clear(); await refresh(); nav(to, { replace: true });
  }

  const notices = <LoginNotices />;
  const footer = (
    <div className="flex items-center justify-center gap-3">
      {!isTenantHostLocked() && <a href="/login">다른 조직으로 로그인</a>}
    </div>
  );
  const back = () => { setStart(null); setPassword(''); setError(null); setLink(null); };

  if (!start) {
    return (
      <AuthShell title="로그인" subtitle="업무용 이메일로 계속합니다." lockTenant steps={{ total: 2, current: 1 }} footer={footer} below={notices}>
        <form onSubmit={onStart}>
          <Field label="이메일"><input className={inputCls} type="email" autoComplete="username" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></Field>
          <AuthButton busy={busy === 'start'} busyText="확인 중…">다음</AuthButton>
          <ErrorText error={error} />
          <SsoErrorBox />
        </form>
      </AuthShell>
    );
  }
  if (link !== null) {
    return (
      <AuthShell title="메일을 확인하세요" lockTenant steps={{ total: 2, current: 2 }} footer={footer} below={notices}>
        <MagicLinkWait email={start.email} minutes={link} onVerified={afterLink} onBack={() => setLink(null)} />
      </AuthShell>
    );
  }
  const ssoAvail = !!start.sso?.available;
  const ssoOnly = ssoAvail && !!start.sso?.required; // 조직이 이메일·비밀번호 로그인을 끔
  return (
    <AuthShell title="로그인 방법 선택" subtitle={ssoOnly ? '이 조직은 SSO 로 로그인합니다.' : undefined} lockTenant steps={{ total: 2, current: 2 }} footer={footer} below={notices}>
      <EmailRow email={start.email} onChange={back} />
      {ssoAvail && <AuthButton type="button" busy={busy === 'sso'} busyText="IdP 로 이동 중…" onClick={onSso}>{start.sso!.displayName}</AuthButton>}
      {ssoOnly ? (
        <>
          <ErrorText error={error} />
          <div className="mt-4 text-[12px]"><button type="button" className="text-slate-500 hover:underline" onClick={() => setShowBreakGlass((v) => !v)}>{showBreakGlass ? '비상 로그인 닫기' : '비상 로그인 (break-glass 관리자)'}</button></div>
          {showBreakGlass && (
            <form onSubmit={onPassword} className="mt-2">
              <Field label="비밀번호"><PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus /></Field>
              <AuthButton busy={busy === 'pw'} done={done} busyText="확인 중…" doneText="확인됨">비상 로그인</AuthButton>
            </form>
          )}
        </>
      ) : (
        <>
          {ssoAvail && <Divider>또는 이메일로</Divider>}
          <form onSubmit={onPassword}>
            <Field label="비밀번호"><PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus={!ssoAvail} /></Field>
            <AuthButton className={ssoAvail ? 'bg-white! text-slate-700! border border-slate-300 hover:bg-slate-50!' : ''} busy={busy === 'pw'} done={done} busyText="로그인 중…" doneText="확인됨">{ssoAvail ? '비밀번호로 로그인' : '로그인'}</AuthButton>
          </form>
          <button type="button" className="mt-3 text-[12px] text-accent-strong hover:underline disabled:opacity-50" disabled={busy === 'link'} onClick={onLink}>{busy === 'link' ? '보내는 중…' : '비밀번호를 잊었거나 없다면 메일로 로그인 링크 받기'}</button>
          <ErrorText error={error} />
        </>
      )}
    </AuthShell>
  );
}

/** 조직 주소가 틀렸을 때 서버가 주는 "테넌트 컨텍스트 없음" 류 오류를 사용자 말로 바꾼다 */
function describeTenantError(err: unknown): unknown {
  if (err instanceof ApiError && err.status === 403 && (err.body as { error?: string })?.error === 'tenant_suspended') return new Error(err.message);
  if (err instanceof ApiError && (err.status === 401 || err.status === 404) && /테넌트|tenant/i.test(err.message)) {
    return new Error('조직을 찾을 수 없습니다. 주소를 다시 확인하거나 "다른 조직으로 로그인" 으로 이메일 로그인을 이용하세요.');
  }
  return err;
}
